/**
 * Conversations domain — store integration layer.
 *
 * Extracted from storeIntegration.ts to isolate conversation loading,
 * pagination, search, activation, and deletion.
 */

import ChatService, { MessageRole } from '@/services/chat';
import {
  getAllConversations,
  getConversationsPaginated,
  searchConversations,
  deleteConversation,
  activateConversation,
  loadConversationInstantly,
  toggleStar,
  updateConversationTitle,
  regenerateConversationTitle,
} from '@/services/conversations';
import { logger } from '@/utils/logger';
import { sentAtFrom } from '@features/chat/utils/messageTime';
import { pickActivityFields } from '@features/conversations/conversationReplay';

import { useAppStore } from './useAppStore';

import type { Conversation, Message, PaginationState } from './useAppStore';
import type { ProcessedMessage } from '@features/conversations/conversations';

/**
 * Backend pagination response shape.
 */
interface BackendPagination {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

const transformPagination = (backendPagination: BackendPagination): PaginationState => ({
  page: backendPagination.page,
  pageSize: backendPagination.page_size,
  totalItems: backendPagination.total_items,
  totalPages: backendPagination.total_pages,
  hasNext: backendPagination.has_next,
  hasPrevious: backendPagination.has_previous,
});

// Track when we're manually activating a conversation to avoid duplicate loading
let isManuallyActivatingConversation = false;

/** Reset the manual activation flag (called from setupServiceListeners). */
export const resetManualActivationFlag = () => {
  isManuallyActivatingConversation = false;
};

/** Check if a manual activation is in progress. */
export const isManualActivationInProgress = () => isManuallyActivatingConversation;

/**
 * Bumped whenever the user abandons the conversation being loaded (New chat).
 * `activateConversation` captures it before its awaits and drops its result if
 * the number moved — otherwise a load that resolves after the reset would
 * repopulate the transcript the user just cleared.
 */
let activationGenerationCount = 0;

/** The current activation generation — read by `startNewChat` and its tests. */
export const activationGeneration = () => activationGenerationCount;

/** Abandon whatever conversation load is in flight (New chat pressed). */
export const abandonConversationLoad = () => {
  activationGenerationCount += 1;
};

/**
 * Counts history loads into the store. The chat page's reconnect refresh reads
 * it: a load made while the socket was down already holds what the drop missed,
 * so the socket coming (back) up must not fetch the same history again.
 */
let historyLoadCount = 0;

/** How many history loads into the store have started this session. */
export const historyLoads = () => historyLoadCount;

/**
 * Load the server's active conversation and its history into the store.
 *
 * Called by the chat surface (see activeConversationHydration), never at boot.
 * Resolves `false` when the load failed, so the caller can retry later. A
 * result is dropped if, while it was in flight, New chat abandoned it or the
 * store moved to a different conversation (one picked from a list) — the
 * hydration must never overwrite a choice the user made meanwhile.
 */
export const initializeActiveConversation = async (): Promise<boolean> => {
  const store = useAppStore.getState();
  const generation = activationGenerationCount;
  const startedOnId = store.activeConversation?.id ?? null;
  const superseded = () =>
    generation !== activationGenerationCount ||
    (useAppStore.getState().activeConversation?.id ?? null) !== startedOnId;

  try {
    logger.info('🔄 Loading active conversation...');
    store.setIsLoadingConversation(true);

    const conversation = await ChatService.getActiveConversation();

    if (!conversation) {
      if (!superseded()) store.clearMessages();
      return true;
    }

    historyLoadCount += 1;
    const { messages: processedMessages } = await loadConversationInstantly(conversation.id);

    if (superseded()) {
      logger.info('↩︎ Discarding an active-conversation load the user moved past');
      return true;
    }

    const metadata = { ...conversation, isStarred: !!conversation.isStarred };
    store.setActiveConversation(buildActiveConversation(conversation.id, metadata));

    // Shared mapper so the render format (and artifacts) survive.
    store.setMessages(toStoreMessages(conversation.id, processedMessages));
    return true;
  } catch (error) {
    logger.error('❌ Error loading active conversation:', error);
    return false;
  } finally {
    store.setIsLoadingConversation(false);
  }
};

/**
 * Initialize conversations list from conversations service
 */
const initializeConversations = async () => {
  const store = useAppStore.getState();

  try {
    store.setIsLoadingConversations(true);

    const conversations = await getAllConversations();
    const transformedConversations = conversations.map(conv => ({
      ...conv,
      messages: [] as Message[],
    }));

    store.setConversations(transformedConversations);
  } catch (error) {
    logger.error('Error loading conversations:', error);
  } finally {
    store.setIsLoadingConversations(false);
  }
};

/**
 * Initialize conversations list with pagination
 */
const initializeConversationsPaginated = async (page: number = 1) => {
  const store = useAppStore.getState();

  try {
    store.setIsLoadingConversations(true);

    const { conversations, pagination } = await getConversationsPaginated(page, 30);
    const transformedConversations = conversations.map(conv => ({
      ...conv,
      messages: [] as Message[],
    }));

    store.setConversations(transformedConversations);
    store.setConversationsPagination(transformPagination(pagination));
  } catch (error) {
    logger.error('Error loading conversations:', error);
  } finally {
    store.setIsLoadingConversations(false);
  }
};

/**
 * Initialize page conversations (separate from left panel conversations)
 */
const initializePageConversationsPaginated = async (page: number = 1) => {
  const store = useAppStore.getState();

  try {
    store.setIsLoadingPageConversations(true);

    const { conversations, pagination } = await getConversationsPaginated(page, 10);
    const transformedConversations = conversations.map(conv => ({
      ...conv,
      messages: [] as Message[],
    }));

    store.setPageConversations(transformedConversations);
    store.setPageConversationsPagination(transformPagination(pagination));
  } catch (error) {
    logger.error('Error loading page conversations:', error);
  } finally {
    store.setIsLoadingPageConversations(false);
  }
};

/** Deactivate all conversations except the given id and mark it active. */
function deactivateOtherConversations(
  store: ReturnType<typeof useAppStore.getState>,
  activeId: string
) {
  store.updateConversation(activeId, { isActive: true });
  store.conversations.forEach(conv => {
    if (conv.id !== activeId && conv.isActive) {
      store.updateConversation(conv.id, { isActive: false });
    }
  });
}

type ConversationOptionalKeys = Pick<Conversation, 'date' | 'lastMessageAt' | 'modelId'>;

/** Collect only the metadata fields that are not `undefined` (exactOptionalPropertyTypes). */
function pickDefinedConversationOptionals(
  metadata: Partial<ConversationOptionalKeys> | undefined
): ConversationOptionalKeys {
  const out: ConversationOptionalKeys = {};
  if (metadata?.date !== undefined) out.date = metadata.date;
  if (metadata?.lastMessageAt !== undefined) out.lastMessageAt = metadata.lastMessageAt;
  if (metadata?.modelId !== undefined) out.modelId = metadata.modelId;
  return out;
}

/** Build a Conversation object from list metadata for setActiveConversation. */
function buildActiveConversation(
  id: string,
  metadata?: Pick<Conversation, 'title' | 'date' | 'lastMessageAt' | 'isStarred' | 'modelId'>
): Conversation {
  return {
    id,
    title: metadata?.title || `Conversation ${id}`,
    isActive: true,
    isStarred: metadata?.isStarred ?? false,
    messages: [] as Message[],
    ...pickDefinedConversationOptionals(metadata),
  };
}

/**
 * Map processed messages to store Message format.
 *
 * The single source of truth for the history → store transform. Every path
 * that hydrates persisted messages (nav-click activate, app-bootstrap active
 * load, reconnect refresh) must route through here so none silently drops a
 * field — `format` was lost on the bootstrap path precisely because it had a
 * hand-rolled copy of this mapping.
 */
export function toStoreMessages(
  conversationId: string,
  processed: ProcessedMessage[]
): Message[] {
  return processed.map((msg, index) => {
    const documentArtifacts = msg.artifacts ?? [];
    const fileArtifacts = (msg.fileArtifacts ?? []).map(fa => ({
      id: fa.artifact_id,
      type: 'file' as const,
      thumbUrl: '',
      name: fa.filename,
      mimeType: fa.mime_type,
      size: fa.size,
    }));
    const artifacts = [...documentArtifacts, ...fileArtifacts];

    return {
      id: `${conversationId}-msg-${index}`,
      role: msg.role === 'user' ? MessageRole.USER : MessageRole.ASSISTANT,
      content: msg.content,
      isStreaming: false,
      interrupted: msg.interrupted,
      // Carry the render format so reloaded html turns render via HtmlProse's
      // history (innerHTML) path. htmlEvents is intentionally omitted — reloaded
      // turns render from the stored content string, not live events.
      ...(msg.format ? { format: msg.format } : {}),
      // The row id, so Retry and Edit can name this message to the server.
      ...(msg.message_id ? { serverMessageId: msg.message_id } : {}),
      // Merge document artifacts (already MessageArtifact-shaped) and mapped
      // file artifacts. Document artifacts come first as they are the primary
      // content; file artifacts are secondary attachments.
      ...(artifacts.length ? { artifacts } : {}),
      ...pickActivityFields(msg),
      ...sentAtFrom(msg.timestamp),
    };
  });
}

/**
 * Apply a partial conversation patch across every store slice that holds a
 * conversation record — the nav history list, the /conversations page list,
 * the search results, AND the active conversation (which the chat header
 * reads) — so they all stay in sync. Without the active-conversation patch the
 * header title goes stale until the page is remounted.
 *
 * @param id - The conversation ID to patch.
 * @param patch - The partial conversation fields to merge.
 */
function patchConversationEverywhere(id: string, patch: Partial<Conversation>) {
  const store = useAppStore.getState();
  store.updateConversation(id, patch);
  store.setPageConversations(
    store.pageConversations.map(conv => (conv.id === id ? { ...conv, ...patch } : conv))
  );
  store.setConversationSearchResults(
    store.conversationSearchResults.map(conv => (conv.id === id ? { ...conv, ...patch } : conv))
  );
  if (store.activeConversation?.id === id) {
    store.patchActiveConversation(patch);
  }
}

/**
 * Enhanced conversations service that integrates with store
 */
export const conversationsService = {
  refreshConversations: async (page?: number) => {
    if (page !== undefined) {
      await initializeConversationsPaginated(page);
    } else {
      await initializeConversations();
    }
  },

  refreshConversationsPaginated: async (page: number = 1) => {
    await initializeConversationsPaginated(page);
    const store = useAppStore.getState();
    store.clearConversationSearch();
  },

  refreshPageConversationsPaginated: async (page: number = 1) => {
    await initializePageConversationsPaginated(page);
    const store = useAppStore.getState();
    store.clearConversationSearch();
  },

  searchConversations: async (query: string, page: number = 1) => {
    const store = useAppStore.getState();

    try {
      store.setIsSearchingConversations(true);
      store.setConversationSearchQuery(query);
      store.setError(null);

      const { conversations, pagination } = await searchConversations(query, page, 10);

      const transformedConversations = conversations.map(conv => ({
        ...conv,
        messages: [] as Message[],
      }));

      store.setConversationSearchResults(transformedConversations);
      store.setConversationSearchPagination(transformPagination(pagination));
    } catch (error) {
      logger.error('Error searching conversations:', error);
      store.setError(error instanceof Error ? error.message : 'Failed to search conversations');
    } finally {
      store.setIsSearchingConversations(false);
    }
  },

  deleteConversation: async (id: string) => {
    const store = useAppStore.getState();
    try {
      store.removeConversation(id);
      await deleteConversation(id);

      logger.info('Conversation deleted successfully');
      store.setError(null);
    } catch (error) {
      await initializeConversations();
      store.setError(error instanceof Error ? error.message : 'Failed to delete conversation');

      logger.error(
        'Failed to delete conversation',
        error instanceof Error ? error.message : undefined
      );

      throw error;
    }
  },

  activateConversation: async (id: string) => {
    const store = useAppStore.getState();
    // Captured before the awaits: New chat bumps it, and a load the user has
    // walked away from must not write into the cleared store.
    const generation = activationGenerationCount;
    try {
      logger.info('⚡ Activating conversation with instant loading:', id);

      isManuallyActivatingConversation = true;

      store.setIsLoadingConversation(true);

      await activateConversation(id);

      deactivateOtherConversations(store, id);

      historyLoadCount += 1;
      const { messages: processedMessages } = await loadConversationInstantly(id);

      if (generation !== activationGenerationCount) {
        logger.info('↩︎ Discarding a conversation load abandoned by New chat', { id });
        return;
      }

      logger.info('📦 Instant loaded conversation:', {
        messageCount: processedMessages.length,
      });

      const conversationMetadata = store.conversations.find(c => c.id === id);
      store.setActiveConversation(
        buildActiveConversation(id, conversationMetadata)
      );
      store.setMessages(toStoreMessages(id, processedMessages));

      logger.info('✅ Conversation activated instantly');
      store.setError(null);
    } catch (error) {
      isManuallyActivatingConversation = false;

      store.setError(error instanceof Error ? error.message : 'Failed to activate conversation');

      logger.error(
        'Failed to activate conversation',
        error instanceof Error ? error.message : undefined
      );

      throw error;
    } finally {
      store.setIsLoadingConversation(false);
    }
  },

  toggleStar: async (id: string) => {
    const store = useAppStore.getState();
    try {
      const isStarred = await toggleStar(id);

      // Patch every slice — list, page, search results, AND the active
      // conversation — so the /chat header star reflects the new state
      // immediately instead of going stale until remount.
      patchConversationEverywhere(id, { isStarred });

      store.setError(null);
    } catch (error) {
      store.setError(error instanceof Error ? error.message : 'Failed to toggle star');
      logger.error('Failed to toggle star', error instanceof Error ? error.message : undefined);
      throw error;
    }
  },

  renameConversation: async (id: string, title: string) => {
    const store = useAppStore.getState();
    try {
      const newTitle = await updateConversationTitle(id, title);
      patchConversationEverywhere(id, { title: newTitle });
      store.setError(null);
      return newTitle;
    } catch (error) {
      store.setError(error instanceof Error ? error.message : 'Failed to rename conversation');
      logger.error(
        'Failed to rename conversation',
        error instanceof Error ? error.message : undefined
      );
      throw error;
    }
  },

  regenerateTitle: async (id: string) => {
    const store = useAppStore.getState();
    try {
      const newTitle = await regenerateConversationTitle(id);
      patchConversationEverywhere(id, { title: newTitle });
      store.setError(null);
      return newTitle;
    } catch (error) {
      store.setError(error instanceof Error ? error.message : 'Failed to regenerate title');
      logger.error(
        'Failed to regenerate title',
        error instanceof Error ? error.message : undefined
      );
      throw error;
    }
  },
};
